import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';

import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';

import VM from 'openblock-vm';

import {setStageSize} from '../reducers/stage-size';
import {STAGE_SIZE_MODES, STAGE_DISPLAY_SIZES} from '../lib/layout-constants';
import {openUploadProgress, closeUploadProgress, setUploadProgressMessage} from '../reducers/modals';
import {showAlertWithTimeout} from '../reducers/alerts';

import HardwareHeaderComponent from '../components/hardware-header/hardware-header.jsx';
import {getLabcodeStudentToken} from '../lib/labcode-student-session';
import {isLabcodeUploadAuthRequired} from '../lib/labcode-auth-config';
import {ensureWebSerialForUpload} from '../lib/web-serial-port';

// Ponte habilitada apenas se a URL da API estiver definida no build (evita carregar web-arduino-uploader na abertura)
const isPonteUploadEnabled = () => {
    const url = typeof process !== 'undefined' && process.env && process.env.REACT_APP_LABCODE_API_URL;
    return Boolean(url && String(url).replace(/\/$/, '').length > 0);
};

// Mapeamento deviceId (Labcode) -> FQBN (compilação) e boardType (gravação Web Serial)
const PONTE_BOARD_MAP = {
    arduinoUno: { boardFqbn: 'arduino:avr:uno', boardType: 'uno' },
    arduinoMega2560: { boardFqbn: 'arduino:avr:mega:cpu=atmega2560', boardType: 'mega2560' },
    arduinoNano: { boardFqbn: 'arduino:avr:nano:cpu=atmega328old', boardType: 'nanoOldBootloader' },
    // Fallback para outros dispositivos AVR
    default: { boardFqbn: 'arduino:avr:uno', boardType: 'uno' }
};

const getPonteBoardOptions = (deviceId) => {
    const key = deviceId && PONTE_BOARD_MAP[deviceId] ? deviceId : 'default';
    return PONTE_BOARD_MAP[key];
};

class HardwareHeader extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleUpload',
            'handleUploadViaPonte'
        ]);
    }

    handleUploadViaPonte () {
        const code = this.props.codeEditorValue;
        if (!code || !code.trim()) {
            this.props.onWorkspaceIsEmpty();
            return;
        }
        this.props.onOpenUploadProgress();
        this.props.onSetUploadProgressMessage('Preparando porta USB...');

        let timeoutId = null;
        let cancelled = false;
        let lastStatus = 'Preparando porta USB...';
        const uploadStartedAt = Date.now();

        const clearTimeoutAndClose = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        const boardOptions = getPonteBoardOptions(this.props.deviceId);
        const isMegaUpload = boardOptions.boardType === 'mega2560';
        const maxUploadMs = isMegaUpload ? 360000 : 120000;
        const stallMs = isMegaUpload ? 120000 : 90000;

        const getTimeoutMessage = () => {
            if (/gravando/i.test(lastStatus)) {
                return (
                    `Tempo esgotado durante a gravação na placa (${lastStatus}). ` +
                    'Feche monitores serial e outros programas na COM, reconecte o cabo USB e tente de novo. ' +
                    'Na Mega, pressione Reset quando aparecer "sincronizando bootloader".'
                );
            }
            if (/compilando/i.test(lastStatus)) {
                return (
                    'Tempo esgotado na compilação remota. Verifique se o servidor em ' +
                    'https://api.labcriar.com.br está online e tente novamente.'
                );
            }
            return (
                'Tempo esgotado no upload. Verifique a conexão USB, o servidor de compilação ' +
                '(REACT_APP_LABCODE_API_URL) e tente novamente.'
            );
        };

        const scheduleUploadTimeout = () => {
            clearTimeoutAndClose();
            if (Date.now() - uploadStartedAt >= maxUploadMs) {
                cancelled = true;
                this.props.onCloseUploadProgress();
                this.props.onPonteUploadError(getTimeoutMessage());
                return;
            }
            timeoutId = setTimeout(() => {
                if (cancelled) return;
                cancelled = true;
                clearTimeoutAndClose();
                this.props.onCloseUploadProgress();
                this.props.onPonteUploadError(getTimeoutMessage());
            }, stallMs);
        };

        scheduleUploadTimeout();
        // Web Serial: pedir/selecionar porta no gesto do clique, antes da compilação (demora segundos).
        const uploadPromise = ensureWebSerialForUpload()
            .then(() => import('../lib/ponte-upload-service'))
            .then(module => {
                const uploadViaPonte = module.uploadViaPonte || (module.default && module.default.uploadViaPonte);
                if (typeof uploadViaPonte !== 'function') {
                    throw new Error('Módulo da Ponte não carregou corretamente.');
                }
                return uploadViaPonte(code, {
                    boardFqbn: boardOptions.boardFqbn,
                    boardType: boardOptions.boardType,
                    onStatus: (msg) => {
                        if (cancelled) return;
                        lastStatus = msg || lastStatus;
                        scheduleUploadTimeout();
                        if (this.props.onSetUploadProgressMessage) {
                            this.props.onSetUploadProgressMessage(msg);
                        }
                    }
                });
            });

        uploadPromise
            .then(() => {
                clearTimeoutAndClose();
                if (cancelled) return;
                this.props.onCloseUploadProgress();
                this.props.onPonteUploadSuccess();
                // Reabre a serial para o monitor (fluxo Ponte não usa OpenBlock Link)
                import('../lib/ponte-serial-monitor').then(({ startPonteSerialMonitorAfterFlash, extractSerialBeginBaud }) => {
                    const fromCode = extractSerialBeginBaud(this.props.codeEditorValue);
                    const baud = fromCode || parseInt(this.props.hardwareConsoleBaudrate, 10) || 9600;
                    startPonteSerialMonitorAfterFlash(this.props.vm, {
                        deviceId: this.props.deviceId,
                        baudRate: baud
                    });
                });
            })
            .catch(err => {
                clearTimeoutAndClose();
                if (cancelled) return;
                this.props.onCloseUploadProgress();
                this.props.onPonteUploadError(err && err.message ? err.message : String(err));
            });
    }

    handleUpload () {
        // Ponto de interceptação: chamado quando o usuário clica em "Upload".
        const uploadContext = {
            codeEditorValue: this.props.codeEditorValue,
            deviceId: this.props.deviceId,
            peripheralName: this.props.peripheralName,
            vm: this.props.vm
        };
        if (typeof this.props.onBeforeUpload === 'function') {
            const continueDefault = this.props.onBeforeUpload(uploadContext);
            if (continueDefault === false) return;
        }

        // Fase 4: Se a Ponte estiver configurada (REACT_APP_LABCODE_API_URL em HTTPS),
        // usa compilação remota + Web Serial em vez do periférico conectado.
        if (isPonteUploadEnabled()) {
            if (isLabcodeUploadAuthRequired() && !getLabcodeStudentToken()) {
                this.props.onUploadNeedLabcodeLogin();
                return;
            }
            this.handleUploadViaPonte();
            return;
        }

        if (this.props.peripheralName) {
            const blocklyBlockCanvas = document.querySelector('.blocklyWorkspace .blocklyBlockCanvas');
            if (blocklyBlockCanvas.childNodes.length === 0) {
                this.props.onWorkspaceIsEmpty();
            } else {
                this.props.vm.uploadToPeripheral(this.props.deviceId, this.props.codeEditorValue);
                this.props.onOpenUploadProgress();
            }
        } else {
            // Upload sem periférico e sem Ponte: mensagem que explica as duas opções
            this.props.onUploadNeedDeviceOrPonte();
        }
    }

    render () {
        const {
            ...props
        } = this.props;
        return (
            <HardwareHeaderComponent
                onUpload={this.handleUpload}
                {...props}
            />
        );
    }
}

HardwareHeader.propTypes = {
    codeEditorValue: PropTypes.string,
    deviceId: PropTypes.string,
    onBeforeUpload: PropTypes.func,
    onNoPeripheralIsConnected: PropTypes.func,
    onCloseUploadProgress: PropTypes.func,
    onOpenUploadProgress: PropTypes.func,
    onSetUploadProgressMessage: PropTypes.func,
    onUploadNeedDeviceOrPonte: PropTypes.func.isRequired,
    onUploadNeedLabcodeLogin: PropTypes.func.isRequired,
    onPonteUploadError: PropTypes.func,
    onPonteUploadSuccess: PropTypes.func,
    hardwareConsoleBaudrate: PropTypes.string,
    onWorkspaceIsEmpty: PropTypes.func.isRequired,
    peripheralName: PropTypes.string,
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    codeEditorValue: state.scratchGui.code.codeEditorValue,
    deviceId: state.scratchGui.device.deviceId,
    peripheralName: state.scratchGui.connectionModal.peripheralName,
    hardwareConsoleBaudrate: state.scratchGui.hardwareConsole.baudrate
});

const mapDispatchToProps = dispatch => ({
    onNoPeripheralIsConnected: () => showAlertWithTimeout(dispatch, 'connectAPeripheralFirst'),
    onUploadNeedDeviceOrPonte: () => showAlertWithTimeout(dispatch, 'uploadNeedDeviceOrPonte'),
    onUploadNeedLabcodeLogin: () => showAlertWithTimeout(dispatch, 'labcodeLoginRequiredForUpload'),
    onSetStageLarge: () => dispatch(setStageSize(STAGE_SIZE_MODES.large)),
    onSetStageSmall: () => dispatch(setStageSize(STAGE_SIZE_MODES.small)),
    onSetStageHide: () => dispatch(setStageSize(STAGE_SIZE_MODES.hide)),
    onCloseUploadProgress: () => dispatch(closeUploadProgress()),
    onOpenUploadProgress: () => dispatch(openUploadProgress()),
    onSetUploadProgressMessage: (message) => dispatch(setUploadProgressMessage(message)),
    onPonteUploadSuccess: () => showAlertWithTimeout(dispatch, 'uploadSuccess'),
    onPonteUploadError: (message) => {
        dispatch({
            type: 'scratch-gui/alerts/SHOW_ALERT',
            alertId: 'uploadError',
            data: { message: message || 'Erro no upload.' }
        });
    },
    onWorkspaceIsEmpty: () => showAlertWithTimeout(dispatch, 'workspaceIsEmpty')
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(HardwareHeader);
